"use client";

import { Skeleton } from "@/components/ui/skeleton";
import { googleMapsKey } from "@/utils/helpers";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from '@capacitor/geolocation';
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  FaLocationArrow,
  FaExpand,
  FaCompress,
  FaUser,
  FaBars,
} from "react-icons/fa";

interface DriverRouteMapProps {
  userPoint: google.maps.LatLngLiteral;
  setDriverPoint: Dispatch<SetStateAction<google.maps.LatLngLiteral | null>>
  driverPoint: google.maps.LatLngLiteral | null;

}

const GOOGLE_MAPS_LIBRARIES: Array<"places"> = ["places"];
interface LatLngLiteral {
  lat: number;
  lng: number;
}

export default function DriverRouteMap({ userPoint, driverPoint, setDriverPoint }: DriverRouteMapProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [zoom, setZoom] = useState(14);
  const [mapType, setMapType] = useState<"roadmap" | "hybrid">("roadmap");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });



  const handleGetLocation = async () => {
  try {
    let newDriverLocation;

    if (Capacitor.isNativePlatform()) {
      // Native platform (iOS/Android) using Capacitor
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
      });
      newDriverLocation = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
    } else {
      // Web platform using navigator.geolocation
      newDriverLocation = await new Promise<LatLngLiteral>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          (error) => {
            reject(error);
          }
        );
      });
    }

    setDriverPoint(newDriverLocation);
    handlePanTo(newDriverLocation);
  } catch (error) {
    console.error("Error getting location:", error);
  }
};


  // Recalculate route when driver or user point changes
  useEffect(() => {
    if (!driverPoint || !userPoint || !isLoaded) return;

    const directionsService = new google.maps.DirectionsService();
    directionsService.route(
      {
        origin: driverPoint,
        destination: userPoint,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          setDirections(result);

          const bounds = new google.maps.LatLngBounds();
          result.routes[0].legs.forEach((leg) => {
            bounds.extend(leg.start_location);
            bounds.extend(leg.end_location);
          });
          mapRef.current?.fitBounds(bounds);
        } else {
          console.error("Directions request failed:", status);
        }
      }
    );
  }, [driverPoint, userPoint, isLoaded]);


  const toggleFullscreen = () => {
    if (!mapContainerRef.current) return;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      document.body.style.overflow = isFullscreen ? "auto" : "hidden";
      setIsFullscreen(!isFullscreen);
    } else {
      if (document.fullscreenElement) {
        document.exitFullscreen();
        setIsFullscreen(false);
      } else {
        mapContainerRef.current.requestFullscreen().then(() => setIsFullscreen(true));
      }
    }
  };

  const handleZoom = (direction: "in" | "out") => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom() || zoom;
      const newZoom = direction === "in" ? Math.min(currentZoom + 1, 21) : Math.max(currentZoom - 1, 3);
      mapRef.current.setZoom(newZoom);
      setZoom(newZoom);
    }
  };

  const handlePanTo = (point: google.maps.LatLngLiteral) => {
    if (mapRef.current) {
      mapRef.current.panTo(point);
      mapRef.current.setZoom(18);
      setZoom(18);
    }
  };

  

  if (!isLoaded || !driverPoint) return (<>
    <Skeleton className="h-[400px] w-full dark:bg-slate-300" />

  </>);

  const containerClass = `relative rounded overflow-hidden ${isFullscreen ? "fixed top-0 left-0 w-screen h-screen z-50" : "h-[400px] w-full"}`;

  return (
    <div className={containerClass} ref={mapContainerRef}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        onLoad={(map) => {
          mapRef.current = map;
          map.panTo(driverPoint);
          map.setZoom(zoom);
        }}
        center={driverPoint}
        zoom={zoom}
        options={{
          disableDefaultUI: true,
          clickableIcons: false,
          scrollwheel: false,
          mapTypeId: mapType,
          gestureHandling: isFullscreen ? "greedy" : "auto",
        }}
      >
        <Marker position={driverPoint} icon={{ url: "http://maps.google.com/mapfiles/kml/shapes/motorcycling.png", scaledSize: new window.google.maps.Size(30, 30), }} />
        <Marker position={userPoint} label="Customer" icon={{ url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png" }} />
        {directions && <DirectionsRenderer directions={directions} options={{ suppressMarkers: true, polylineOptions: { strokeColor: "#1A73E8", strokeWeight: 5 } }} />}
      </GoogleMap>

      {/* Controls */}
      <div className="absolute top-2 right-2 z-10">
        <div className="relative">
          <button onClick={() => setMenuOpen((prev) => !prev)} className="bg-white rounded shadow w-10 h-10 flex items-center justify-center hover:bg-gray-100">
            <FaBars />
          </button>

          {menuOpen && (
            <div className="absolute top-12 right-0 flex flex-col gap-2 bg-white p-2 rounded-md shadow-lg z-20">
              <div className="flex gap-1">
                <button onClick={() => handlePanTo(userPoint)} className="bg-white rounded shadow w-10 h-10 flex items-center justify-center hover:bg-gray-100" title="Zoom to User">
                  <FaUser />
                </button>
                <button
                  onClick={handleGetLocation}
                  className="bg-white rounded shadow w-10 h-10 flex items-center justify-center hover:bg-gray-100"
                  title="My Location"
                >
                  <FaLocationArrow />
                </button>

              </div>
              <div className="flex gap-1">
                <button onClick={() => handleZoom("in")} className="bg-white rounded shadow w-10 h-10 flex items-center justify-center hover:bg-gray-100" title="Zoom In">+</button>
                <button onClick={() => handleZoom("out")} className="bg-white rounded shadow w-10 h-10 flex items-center justify-center hover:bg-gray-100" title="Zoom Out">−</button>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setMapType("roadmap")} className={`bg-white rounded shadow p-1 h-10 w-10 hover:bg-gray-100 ${mapType === "roadmap" && "border-[3px] border-blue-500"}`} title="Roadmap">
                  <img src="/images/roadmap.png" alt="Roadmap" className="w-full h-full object-cover rounded" />
                </button>
                <button onClick={() => setMapType("hybrid")} className={`bg-white rounded shadow p-1 h-10 w-10 hover:bg-gray-100 ${mapType === "hybrid" && "border-[3px] border-blue-500"}`} title="Hybrid">
                  <img src="/images/satellite_720.png" alt="Hybrid" className="w-full h-full object-cover rounded" />
                </button>
              </div>
              <div className="mx-auto">
                <button onClick={toggleFullscreen} className="bg-white rounded shadow w-10 h-10 flex items-center justify-center hover:bg-gray-100" title="Fullscreen">
                  {isFullscreen ? <FaCompress /> : <FaExpand />}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}